import { ReactElement } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../redux/hook";
import { useGetVendeurByApiKeyQuery } from "../utils/api/product/product.api";
import { IUser, UserType } from "../utils/api/user/user.type";
import { VendeurApikey } from "../utils/http";
import { isAcheteur, isAdmin, isPreparateur, isUser } from "../utils/Utils";

interface ProtectedRoutesProps {
  children: ReactElement;
  userType: UserType[];
} 

export const ProtectedRoutes = ({
  children,
  userType,
}: ProtectedRoutesProps) => {
  let location = useLocation();
  const { user, token } = useAppSelector((state) => state.user);

  // Vérifier d'abord si l'utilisateur est authentifié
  if (!token || !user?.id) {
    console.log("🔒 ProtectedRoutes: Utilisateur non authentifié", { token: !!token, userId: !!user?.id });
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Ensuite vérifier le type d'utilisateur
  // Si user_type n'est pas encore chargé, permettre l'accès si l'utilisateur est authentifié
  if (!user?.user_type) {
    console.log("🔒 ProtectedRoutes: user_type non défini, accès autorisé", { user });
    return children;
  }

  // Convertir les enums en strings pour la comparaison (insensible à la casse)
  const allowedTypes = userType?.map(type => String(type).toUpperCase());
  const userTypeString = String(user?.user_type).toUpperCase();

  // Vérifier si le type d'utilisateur est dans la liste des types autorisés
  const isAccess = allowedTypes?.includes(userTypeString);

  console.log("🔒 ProtectedRoutes Debug:", {
    userType: user?.user_type,
    userTypeString,
    allowedTypes,
    isAccess,
    token: !!token,
    userId: !!user?.id,
    isAdmin: isAdmin(user),
    isAcheteur: isAcheteur(user),
    location: location.pathname,
    UserTypeValues: {
      admin: UserType.admin,
      acheteur: UserType.acheteur,
      vendeur: UserType.vendeur,
      superadmin: UserType.superadmin
    }
  });

  return isAccess ? (
    children
  ) : (
    <Navigate to="/" state={{ from: location }} replace />
  );
};

export const RedirectAuthRoute = ({ children }: { children: ReactElement }) => {
  const { token, user } = useAppSelector((state) => state.user);
  let path: string = "/";


  if (isUser(user)) {
    path = "/mon-compte/profil";
  } else if (isAdmin(user)) {
    path = "/produits";
  } else if (isAcheteur(user)) {
    path = "/produits";
  }

  return !token && !user?.id ? children : <Navigate to={path} replace />;
};

// UserRoutes
export const IsUserAllow = ({ children }: { children: ReactElement }) => {
  const { user, token } = useAppSelector((state) => state.user);
  const [params] = useSearchParams();
  let next = params.get("next");
  let path: string = params.get("next") || "/";
  if (token) {
    if (isAdmin(user)) {
      path = "/admin/dashboard";
    }

    if (isAcheteur(user)) {
      path = "/produits";
    }
  }

  // console.log("path",path)

  // return children;
  return !token || !isUser(user) || next ? (
    <Navigate to={path} replace />
  ) : (
    children
  );
};

export const IsAdminAllow = ({ children }: { children: ReactElement }) => {
  const { user, token } = useAppSelector((state) => state.user);
  let path: string = "/connexion";
  if (token) {
    if (isUser(user)) {
      path = "/mon-compte/profil";
    }

    if (isPreparateur(user)) {
      path = "/préparateur/commandes";
    }
  }

  // return children;
  return !token || !isAdmin(user) ? <Navigate to={path} replace /> : children;
};

export const RedirectPageNotAvailable = ({
  children,
}: {
  children: ReactElement;
}) => {
  const { data: vendeur, isLoading, error } = useGetVendeurByApiKeyQuery(VendeurApikey);

  const location = useLocation();
  let path = location.pathname;

  // Si erreur 401 (API key invalide), permettre quand même l'accès (mode dégradé)
  if (error && 'status' in error && error.status === 401) {
    console.warn('⚠️ API key invalide ou vendeur non trouvé en production, mode dégradé activé');
    return children; // Permettre l'accès même sans vendeur
  }

  // Si chargement, attendre
  if (isLoading) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>Chargement...</div>;
  }

  // Si vendeur existe mais n'est pas actif, rediriger
  if (vendeur && !vendeur?.is_active) {
    path = "/site-non-disponible";
    return <Navigate to={path} replace />;
  }

  // Si vendeur actif ou pas de vendeur (mode dégradé), permettre l'accès
  return children;
};
